import {
  Collapse,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  styled,
} from "@mui/material";
import { APP_ICONS, CloseIcon, EmailIcon, SmoothCheckIcon } from "Assets/icons";
import {
  Checkbox,
  ErrorText,
  GrayButton,
  Img,
  Input,
  LgTextMd,
  OpenTabLink,
  PasswordInput,
  Row,
  Select,
  TextButton,
  XsText,
} from "Components";
import { PATHS } from "constants/routes";
import { VALID_EMAIL } from "constants/validate";
import { formatError } from "helpers/error";
import { useLoading } from "providers/loading";
import React, {
  memo,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Controller, useForm, UseFormGetValues } from "react-hook-form";
import { useIntl } from "react-intl";
import { apiActiveAccount } from "services/api/user";
import useAuth from "hooks/useAuth";
import { removeUndefinedFields } from "helpers/format";
import { COLORS } from "constants/layout";
import useBreakpoints from "hooks/useBreakpoint";
import { scrollToTop } from "helpers/browser";
import { useCommonData } from "providers/common-data";
import { OptionType } from "types";
import { useLanguageContext } from "Components/LanguageProvider";
import { apiSearchProductCategory } from "services/api/helpers";
import { Autocomplete } from "Components/input/index";
import { CategoryType } from "types/category";
import BelowSelect from "Components/input/BelowSelect";
import { JoinFormType } from "types/form";

type Props = {
  category?: OptionType;
  onClose?: () => void;
  getData?: UseFormGetValues<any>;
  activateKey?: string;
  onSuccessful?: () => void;
  typeIds?: {
    rfqId?: string;
    inquiryId?: string;
  };
};

const JoinUsForm = (props: Props) => {
  const t = useIntl();
  const {
    getData,
    onClose,
    activateKey,
    onSuccessful,
    typeIds = {},
    category,
  } = props;
  const { setLoading } = useLoading();
  const { onGetUserInformation } = useAuth();
  const { isMobile } = useBreakpoints();
  const { appConfigs } = useCommonData();
  const { rfqId, inquiryId } = typeIds;
  const currentFocusedRef = useRef<HTMLInputElement>(null);
  const { language } = useLanguageContext();

  const [searchCategories, setsearchCategories] = useState<OptionType[]>([]);
  const [agreePolicy, setAgreePolicy] = useState(true);
  const [openFull, setOpenFull] = useState(true);
  const {
    register,
    handleSubmit,
    setError,
    watch,
    control,
    reset,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<JoinFormType>({
    defaultValues: {
      email: "",
      new_password: "",
      fullname: "",
      company_name: "",
      key: activateKey,
      country: "",
      phone: "",
      business_types: [],
      product_category: undefined,
    },
    mode: "onChange",
  });

  const phoneCountryCode = watch("country") || "";
  const business_types: OptionType[] = watch("business_types");

  const countries = useMemo(
    () => appConfigs?.countries || [],
    [appConfigs?.countries]
  );

  const phoneCountryData = useMemo(
    () =>
      countries.find(
        (country: OptionType) => country.value === phoneCountryCode
      ),
    [phoneCountryCode, countries]
  );

  const benefits = [
    {
      label: t.formatMessage(
        {
          id: "by_joining_us_now_you_can_access_to_verified_suppliers",
        },
        {
          s: (code: ReactNode) => <span>{code}</span>,
        }
      ),
    },
    {
      label: t.formatMessage(
        {
          id: "connect_directly_with_verified_suppliers",
        },
        {
          s: (code: ReactNode) => <span>{code}</span>,
        }
      ),
    },
    {
      label: t.formatMessage(
        {
          id: "free_register_no_transaction_fee_free_post_sourcing_request",
        },
        {
          s: (code: ReactNode) => <span>{code}</span>,
        }
      ),
    },
  ];

  const onSubmitForm = async (value: JoinFormType) => {
    try {
      setLoading(true);
      const formatData = removeUndefinedFields({
        ...value,
        rfqId,
        inquiryId,
        language,
        product_category: value?.product_category?.value,
      });
      await apiActiveAccount(formatData);
      await onGetUserInformation();
      onClose?.();
      onSuccessful?.();
    } catch (error) {
      const errorMsg = formatError(error)?.message;
      setError("root", {
        message: errorMsg,
      });
    } finally {
      setLoading(false);
    }
  };

  const getSearchCategories = async (search: string | null) => {
    const res = await apiSearchProductCategory(search);
    setsearchCategories(
      (res.data?.message?.data || []).map((c: CategoryType) => ({
        value: c.name,
        label: c.category_name,
      }))
    );
  };

  const onDeleteSelect = (value: string | number) => {
    setValue(
      "business_types",
      business_types.filter((o: any) => o !== value),
      { shouldDirty: true }
    );
  };

  const onAddSelect = (value: OptionType) => {
    setValue("business_types", [...business_types, value], {
      shouldDirty: true,
    });
  };

  useEffect(() => {
    if (getData) {
      const { email, customer_name, company_name, phone_number, country } =
        getData() || {};
      reset({
        ...getValues(),
        email,
        fullname: customer_name,
        company_name,
        country,
        phone: phone_number,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getData]);

  useEffect(() => {
    if (currentFocusedRef.current) {
      currentFocusedRef.current.focus();
    }
    setTimeout(() => {
      scrollToTop();
    }, 500);
  }, []);

  useEffect(() => {
    if (category) {
      getSearchCategories(category?.label || "");
      setValue("product_category", category);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  return (
    <StyledStack spacing={2} className="main">
      <Row alignItems="flex-start" spacing={1} justifyContent="space-between">
        <div className="tmp" />
        <LgTextMd align="center">
          {t.formatMessage({
            id: rfqId
              ? "activate_account_manage_requests"
              : inquiryId
              ? "activate_account_manage_inquiry"
              : "one_step_to_activate_account",
          })}
        </LgTextMd>
        <IconButton className="fit" onClick={onClose}>
          <CloseIcon width={12} />
        </IconButton>
      </Row>

      <form onSubmit={handleSubmit(onSubmitForm)}>
        <Grid container spacing={{ xs: 1, md: 2 }} mb={2.5}>
          <Grid item xs={12} md={6}>
            <Input
              {...register("email", {
                required: t.formatMessage({ id: "requied_input" }),
                pattern: {
                  value: VALID_EMAIL,
                  message: t.formatMessage({ id: "invalid_email" }),
                },
              })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon />
                  </InputAdornment>
                ),
              }}
              placeholder="email@example.com"
              error={!!errors?.email}
              helperText={errors?.email?.message}
              inputRef={currentFocusedRef}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <PasswordInput
              {...register("new_password", {
                required: t.formatMessage({ id: "requied_input" }),
              })}
              placeholder={t.formatMessage({
                id: "enter_your_password",
              })}
              error={!!errors?.new_password}
              helperText={errors?.new_password?.message}
            />
          </Grid>
        </Grid>

        <div>
          <TextButton
            onClick={() => setOpenFull(!openFull)}
            startIcon={<Img src={APP_ICONS.chevronDown} />}
            className={"show-btn " + (openFull ? "full" : "")}
          >
            {t.formatMessage({ id: "update_your_contact" })}
          </TextButton>

          <Collapse
            in={openFull}
            style={{ marginTop: openFull ? (isMobile ? 8 : 16) : 0 }}
          >
            <Grid container spacing={{ xs: 1, md: 2 }}>
              <Grid item xs={6}>
                <Input
                  {...register("company_name")}
                  placeholder={t.formatMessage({
                    id: "enter_your_company_name",
                  })}
                />
              </Grid>

              <Grid item xs={6}>
                <Input
                  {...register("fullname")}
                  placeholder={t.formatMessage({
                    id: "enter_your_fullname",
                  })}
                />
              </Grid>

              <Grid item xs={6}>
                <FormControl
                  className={phoneCountryCode ? "" : "placeholder"}
                  fullWidth
                >
                  <Controller
                    name="country"
                    control={control}
                    defaultValue={phoneCountryCode}
                    render={({ field }) => (
                      <Select {...field} options={countries} />
                    )}
                  />
                </FormControl>
              </Grid>

              <Grid item xs={6}>
                <Input
                  {...register("phone")}
                  placeholder={t.formatMessage({ id: "phone_number" })}
                  InputProps={{
                    startAdornment: phoneCountryCode && (
                      <InputAdornment position="start">
                        {phoneCountryData?.code?.toUpperCase()} (+
                        {phoneCountryData?.custom_calling_code})
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* Category */}
              <Grid item xs={6}>
                <Autocomplete
                  control={control}
                  name="product_category"
                  placeholder={t.formatMessage({
                    id: "select_product_category",
                  })}
                  options={searchCategories}
                  getOptionLabel={(option) => option?.label}
                  setValue={setValue}
                  onFetchOptions={getSearchCategories}
                />
              </Grid>

              {/* Biz type */}
              <Grid item xs={6}>
                <FormControl
                  className={business_types ? "" : "placeholder"}
                  fullWidth
                >
                  <Controller
                    name="business_types"
                    control={control}
                    defaultValue={[]}
                    render={({ field }) => (
                      <BelowSelect
                        placeholder={t.formatMessage({
                          id: "business_types",
                        })}
                        {...field}
                        options={appConfigs?.businessTypes}
                        customValue={business_types}
                        onChange={(e: any) => onAddSelect(e.target.value)}
                        onDeleteSelect={(v) => onDeleteSelect(v)}
                      />
                    )}
                  />
                </FormControl>
              </Grid>
            </Grid>
          </Collapse>
        </div>

        {!!errors.root && <ErrorText pt={2}>{errors.root?.message}</ErrorText>}

        <Row spacing={1} mt={2}>
          <Checkbox
            checked={agreePolicy}
            onChange={(e) => setAgreePolicy(e.target.checked)}
          />
          <XsText fontSize={10} className="desc-text">
            {t.formatMessage(
              { id: "confirm_policy_register" },
              {
                s: (code: ReactNode) => (
                  <OpenTabLink href={PATHS.termCondition}>{code}</OpenTabLink>
                ),
                ss: (code: ReactNode) => (
                  <OpenTabLink href={PATHS.policy}>{code}</OpenTabLink>
                ),
                sss: (code: ReactNode) => (
                  <OpenTabLink href={PATHS.disclaimers}>{code}</OpenTabLink>
                ),
              }
            )}
          </XsText>
        </Row>

        <Row mt={2} className="button-box">
          <GrayButton
            className="verify-btn"
            type="submit"
            disabled={!agreePolicy}
            fullWidth
          >
            {t.formatMessage({ id: "join_now" })}
          </GrayButton>
        </Row>
      </form>

      <Stack spacing={1}>
        {benefits.map((benefit, i) => (
          <Row spacing={{ xs: 1, md: 2 }} key={i} className="benefit-text">
            <SmoothCheckIcon />
            <XsText>{benefit.label}</XsText>
          </Row>
        ))}
      </Stack>
    </StyledStack>
  );
};

const StyledStack = styled(Stack)(({ theme }) => ({
  padding: 24,
  backgroundColor: "#fff",
  borderRadius: 8,
  ".desc-text": {
    color: "var(--gray-500)",
    a: {
      color: COLORS.green[500],
    },
  },
  ".verify-btn": {
    maxWidth: 188,
  },
  ".button-box": {
    justifyContent: "center",
    ".chat-btn": {
      color: "var(--yellow-500)",
      fontWeight: 600,
    },
    ".tmp": {
      opacity: 0,
    },
  },
  ".benefit-text": {
    svg: {
      minWidth: 18,
    },
    span: {
      color: COLORS.green[700],
    },
  },
  ".show-btn": {
    fontSize: 14,
    fontWeight: 400,
    color: COLORS.gray[900],
    img: {
      transform: "rotate(0)",
    },
    "&.full": {
      img: {
        transform: "rotate(180deg)",
      },
    },
  },
  ".tmp": {
    width: 15,
  },

  [theme.breakpoints.down("md")]: {
    padding: "8px",
    ".desc-text": {
      fontSize: 12,
    },
    ".button-box": {
      ".tmp": {
        display: "none",
      },
    },
    ".verify-btn": {
      maxWidth: 120,
    },
  },
}));

export default memo(JoinUsForm);
